/**
 * Admin (AM-side) client-chat queries. Same endpoints as the client portal, but
 * staff MUST pass ?workspaceId=<current> (BR-13: the backend 403s an AM who is
 * not assigned to it). Sender attribution ('am') is derived server-side.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http, withQuery } from '@teivor/shared-utils/api';

import type { ChatThread, ChatMessage } from './types';

const keys = {
  all: ['com', 'chat'] as const,
  thread: (ws: string) => ['com', 'chat', 'thread', ws] as const,
  messages: (ws: string) => ['com', 'chat', 'messages', ws] as const,
};

/** The thread (lazy-created server-side). Also exposes amUnreadCount for the nav badge. */
export function useChatThread(ws: string | null, opts?: { enabled?: boolean }) {
  return useQuery<ChatThread>({
    queryKey: keys.thread(ws ?? '∅'),
    queryFn: () => http.get<ChatThread>(withQuery('/api/com/client/chat', { workspaceId: ws ?? undefined })),
    enabled: (opts?.enabled ?? true) && !!ws,
    staleTime: 30_000,
  });
}

export function useChatMessages(ws: string | null) {
  return useQuery<ChatMessage[]>({
    queryKey: keys.messages(ws ?? '∅'),
    queryFn: () =>
      http.get<ChatMessage[]>(withQuery('/api/com/client/chat/messages', { workspaceId: ws ?? undefined })),
    enabled: !!ws,
  });
}

export function useSendChatMessage(ws: string | null) {
  const qc = useQueryClient();
  return useMutation<ChatMessage, unknown, { body: string }>({
    mutationFn: ({ body }) =>
      http.post<ChatMessage>(withQuery('/api/com/client/chat/messages', { workspaceId: ws ?? undefined }), { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useMarkChatRead(ws: string | null) {
  const qc = useQueryClient();
  return useMutation<ChatThread, unknown, { upToMessageId: string }>({
    mutationFn: ({ upToMessageId }) =>
      http.patch<ChatThread>(withQuery('/api/com/client/chat/read', { workspaceId: ws ?? undefined }), { upToMessageId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.all }),
  });
}
